// RETAIN — Client Retention Agent dashboard. The operator's window
// into what RETAIN is doing across the closed book: who's at risk,
// what saves are open, what escalated, and how the persistency trend
// looks. Read-only mostly; one write action (resolve escalation).

import React from 'react';
import { Shield, AlertCircle, TrendingUp, Heart, ChevronRight, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useIsDemoMode, useDemoBookOfBusiness, useDemoLeads } from '../demo/DemoDataProvider';

// Read JWT directly from localStorage — bypasses supabase.auth.getSession()
// which can hang mid-refresh on a stale token. Same pattern as
// src/leads/authedFetch.js, src/scorecard/AgentScorecardView.jsx.
function readPersistedJwtDirect() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('sb-') || !k.endsWith('-auth-token')) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const token = parsed?.access_token || parsed?.currentSession?.access_token;
      if (!token) continue;
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload?.exp && payload.exp * 1000 > Date.now() + 60_000) return token;
    }
  } catch { /* fall through */ }
  return null;
}

async function apiFetch(path, init = {}, signal = null) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    signal,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

function fmtAgo(iso) {
  if (!iso) return '';
  const t = Date.parse(iso); if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

const BUCKET_META = {
  critical: { label: 'Critical',  tone: 'red',     bgTone: 'bg-red-50',     borderTone: 'border-red-200',     textTone: 'text-red-800' },
  at_risk:  { label: 'At risk',   tone: 'orange',  bgTone: 'bg-orange-50',  borderTone: 'border-orange-200',  textTone: 'text-orange-800' },
  watch:    { label: 'Watch',     tone: 'amber',   bgTone: 'bg-amber-50',   borderTone: 'border-amber-200',   textTone: 'text-amber-800' },
  healthy:  { label: 'Healthy',   tone: 'emerald', bgTone: 'bg-emerald-50', borderTone: 'border-emerald-200', textTone: 'text-emerald-800' },
};

const URGENCY_META = {
  critical: { label: 'Critical', bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-800',     rank: 3 },
  high:     { label: 'High',     bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800',  rank: 2 },
  normal:   { label: 'Normal',   bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-800',   rank: 1 },
  low:      { label: 'Low',      bg: 'bg-slate-50',  border: 'border-slate-200',  text: 'text-slate-700',   rank: 0 },
};

// Demo Mode snapshot — synthesizes the shape /api/retain/snapshot
// returns so the dashboard renders fully instead of hanging on the
// loader. The shape was reverse-engineered from RetainAgentView's
// reads: summary needs in_force / saves_30d / escalations_open and
// bucket counts as TOP-LEVEL summary keys (not nested); each client
// is FLAT (carrier/name/policy/days_since_issue at root) with health
// nested as { bucket, score, factors: [{ label, impact }] }; each
// escalation has urgency / client_name / summary / suggested_action /
// created_at. Numbers chosen to look like a healthy mid-size agency
// (Sunrise Health & Life).
function buildDemoRetainSnapshot(book, leads) {
  const safeBook = book || { in_force_count: 187, thirteen_mo_persistency_pct: 83.4 };
  void leads; // accept for symmetry; clients are explicit below
  const now = Date.now();
  const demoClients = [
    {
      id: 'd_dc01', client_id: 'd_dc01',
      name: 'David Cherryholmes',
      carrier: 'Mutual of Omaha',
      policy: 'Final Expense · $15K face',
      days_since_issue: 412,
      open_conservation_case: true,
      crosssell_gaps: 2,
      health: {
        bucket: 'critical', score: 18,
        factors: [
          { label: 'Second NSF in 30 days — payment failed twice', impact: -42 },
          { label: 'No response to last 3 outbound texts', impact: -18 },
          { label: 'Policy 13+ months in force', impact: +6 },
        ],
      },
    },
    {
      id: 'd_fd02', client_id: 'd_fd02',
      name: 'Frank DeLuca',
      carrier: 'Humana',
      policy: 'Medicare Advantage · plan H4514',
      days_since_issue: 217,
      open_conservation_case: false,
      crosssell_gaps: 3,
      health: {
        bucket: 'at_risk', score: 42,
        factors: [
          { label: '2026 formulary materially lower — ANOC review needed', impact: -30 },
          { label: 'No ANOC response within 14 days', impact: -10 },
        ],
      },
    },
    {
      id: 'd_eb03', client_id: 'd_eb03',
      name: 'Eric Brown',
      carrier: 'Mutual of Omaha',
      policy: 'Final Expense · $20K face',
      days_since_issue: 88,
      open_conservation_case: false,
      crosssell_gaps: 4,
      health: {
        bucket: 'at_risk', score: 47,
        factors: [
          { label: 'Underwriting pending — carrier requested APS', impact: -25 },
          { label: 'No reply 3 days after last outreach', impact: -12 },
        ],
      },
    },
    {
      id: 'd_rc04', client_id: 'd_rc04',
      name: 'Robert Chen',
      carrier: 'Ambetter',
      policy: 'ACA Silver · BCBS',
      days_since_issue: 152,
      open_conservation_case: false,
      crosssell_gaps: 2,
      health: {
        bucket: 'watch', score: 64,
        factors: [
          { label: 'Income doc due — marketplace re-verification', impact: -16 },
          { label: 'Email + SMS opt-in healthy', impact: +6 },
        ],
      },
    },
    {
      id: 'd_pw05', client_id: 'd_pw05',
      name: 'Patricia Williams',
      carrier: 'Aetna',
      policy: 'Medicare Supplement Plan G',
      days_since_issue: 305,
      open_conservation_case: false,
      crosssell_gaps: 1,
      health: {
        bucket: 'watch', score: 71,
        factors: [
          { label: 'Illinois Birthday Rule window opens in 21 days', impact: -10 },
          { label: 'On autopay · 13+ months in force', impact: +12 },
        ],
      },
    },
    {
      id: 'd_lm06', client_id: 'd_lm06',
      name: 'Linda Martinez',
      carrier: 'Cigna',
      policy: 'Term Life · 20yr / $500K',
      days_since_issue: 410,
      open_conservation_case: false,
      crosssell_gaps: 1,
      health: {
        bucket: 'healthy', score: 92,
        factors: [
          { label: 'Renewed on time · no payment issues 12 mo', impact: +20 },
        ],
      },
    },
  ];
  return {
    generated_at: new Date(now).toISOString(),
    summary: {
      in_force:         safeBook.in_force_count || 187,
      saves_30d:        9,
      escalations_open: 2,
      critical:         3,
      at_risk:          8,
      watch:            21,
      healthy:          155,
      persistency_pct:  safeBook.thirteen_mo_persistency_pct || 83.4,
    },
    clients: demoClients,
    escalations: [
      {
        id: 'd_esc01',
        urgency: 'critical',
        client_name: 'David Cherryholmes',
        policy_ref:  'Mutual of Omaha · FE $15K',
        summary: 'Second NSF in 30 days. RETAIN paused outbound outreach pending operator review.',
        suggested_action: 'Call client → confirm new payment method → re-run draft.',
        created_at: new Date(now - 8 * 3600_000).toISOString(),
      },
      {
        id: 'd_esc02',
        urgency: 'high',
        client_name: 'Frank DeLuca',
        policy_ref:  'Humana MAPD · H4514',
        summary: 'Carrier filed materially lower 2026 formulary. Client meds may not be covered.',
        suggested_action: 'Run ENROLL plan comparison → AEP switch if better fit.',
        created_at: new Date(now - 30 * 3600_000).toISOString(),
      },
    ],
    setup_state: null,
    diagnostic: null,
    is_demo: true,
  };
}

export default function RetainAgentView({ currentUser, showToast = () => {} }) {
  const isDemo  = useIsDemoMode();
  const demoBook  = useDemoBookOfBusiness();
  const demoLeads = useDemoLeads();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  // Carriers catalog lookup so we can show a "Pay portal ↗" link
  // on every at-risk client whose carrier matches the catalog. Per
  // audit response §11 + §15 Phase 5b RETENTION row "carrier
  // payment portal link from within the retention screen."
  const [carriersByName, setCarriersByName] = React.useState({});
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: rows } = await supabase
          .from('carriers')
          .select('display_name, portal_url, autopay_url, customer_service_phone')
          .eq('is_active', true);
        if (!alive) return;
        const map = {};
        for (const r of (rows || [])) {
          const k = String(r.display_name || '').toLowerCase().trim();
          if (k) map[k] = r;
          // Also key by the first word so "Aetna Senior" and "Aetna"
          // both match if the operator typed either.
          const first = k.split(/\s+/)[0];
          if (first && !map[first]) map[first] = r;
        }
        setCarriersByName(map);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) {
      // Arm a 3s watchdog so a permanently-missing user resolves to a
      // signed-out message instead of an infinite "Loading Retain…".
      const t = setTimeout(() => {
        setLoading(false);
        setError('Sign in required to see Retain.');
      }, 3000);
      return () => clearTimeout(t);
    }
    if (isDemo) {
      setData(buildDemoRetainSnapshot(demoBook, demoLeads));
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12_000);
    try {
      const r = await apiFetch('/api/retain/snapshot', {}, controller.signal);
      setData(r);
      setError(null);
    } catch (err) {
      const msg = err?.name === 'AbortError' ? 'request_timed_out' : (err?.message || 'load_failed');
      setError(msg);
      showToast(`Retain load failed: ${msg}`, 'error');
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }, [currentUser?.id, showToast, isDemo, demoBook, demoLeads]);

  React.useEffect(() => { load(); }, [load]);

  const resolveEscalation = async (esc, status, outcome) => {
    try {
      await apiFetch('/api/retain/resolve-escalation', {
        method: 'POST',
        body: JSON.stringify({ id: esc.id, status, outcome }),
      });
      showToast(`Escalation ${status}.`);
      load();
    } catch (err) {
      showToast(`Could not resolve: ${err.message}`, 'error');
    }
  };

  if (loading) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="text-sm text-slate-500">Loading Retain…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-900">
          Retain temporarily unavailable ({error}).
          <button
            onClick={() => load()}
            className="ml-3 text-amber-800 underline hover:text-amber-900 font-semibold"
          >Retry</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="text-sm text-slate-500">No Retain data yet.</div>
      </div>
    );
  }

  // Defensive: server can return {setup_state:'no_data_yet'} without a
  // summary block — spread a zeroed default so `summary.in_force`
  // etc. never crash the render.
  const {
    summary: rawSummary,
    clients = [],
    escalations = [],
    setup_state: setupState,
    diagnostic,
  } = data;
  const summary = {
    in_force: 0, saves_30d: 0, escalations_open: 0,
    critical: 0, at_risk: 0, watch: 0, healthy: 0,
    persistency_pct: 0,
    ...(rawSummary || {}),
  };
  const filteredClients = filter === 'all'
    ? clients
    : clients.filter(c => c.health.bucket === filter);

  // Empty-state guidance per audit response §11 Phase 5 RETENTION
  // "RETAIN agent is watching '0 active clients' — it's not connected
  // to the policy data without manual sync." When the snapshot endpoint
  // returns a `setup_state` other than null, we know WHY there are no
  // active rows and can give an actionable next step.
  const setupBanner = setupState && (() => {
    switch (setupState) {
      case 'transactions_no_clients':
        return {
          tone: 'amber',
          headline: `${diagnostic?.transactions_count || 0} transactions imported but no client records yet`,
          detail: 'RETAIN reads from your Clients table. The carrier transactions you imported need to be synced into Clients before RETAIN can score them. Open Settings → Data & Imports and run "Sync Book of Business," or seed clients from the Book of Business tab.',
        };
      case 'no_data_yet':
        return {
          tone: 'slate',
          headline: 'No book imported yet',
          detail: 'RETAIN scores your in-force life book. Start by importing a carrier statement (Briefing tab) or syncing your Health book — once policy data lands, the buckets below populate automatically.',
        };
      case 'health_only':
        return {
          tone: 'sky',
          headline: 'Your active book is all health policies',
          detail: 'RETAIN is life-only by design — health clients get the AEP/OEP-specific retention engine in the Health portal. Switch to Health Clients to see your retention surface for those clients.',
        };
      case 'all_implausible':
        return {
          tone: 'amber',
          headline: 'All client rows look like test data',
          detail: 'Every clients row was filtered as implausible (e.g. name is "ffffgg" or contains only consonants). Run migration 096 cleanup or replace the test data with a real book.',
        };
      default:
        return null;
    }
  })();

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Empty-state guidance banner — closes audit row §11 row 1
          "RETAIN agent watching '0 active clients'" with a specific
          next action rather than letting the operator stare at zeros. */}
      {setupBanner && (() => {
        const t = setupBanner.tone;
        const toneClass = t === 'amber' ? 'border-amber-300 bg-amber-50 text-amber-900'
                       : t === 'sky'   ? 'border-sky-300 bg-sky-50 text-sky-900'
                       :                 'border-slate-300 bg-slate-50 text-slate-900';
        return (
          <div className={`border-2 rounded-xl px-5 py-4 ${toneClass}`}>
            <div className="font-bold">{setupBanner.headline}</div>
            <div className="text-sm mt-1 opacity-90">{setupBanner.detail}</div>
          </div>
        );
      })()}

      {/* Hero — RETAIN identity + mission stats */}
      <div className="bg-gradient-to-br from-emerald-900 to-slate-900 text-white rounded-xl px-5 py-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-200/80 flex items-center gap-2">
              <Shield size={11}/> RETAIN — Client Retention Agent
            </div>
            <div className="text-lg font-bold mt-1">Keep every closed policy in force.</div>
            <div className="text-xs text-emerald-200/80 mt-0.5">
              Watching {summary.in_force} active clients · {summary.saves_30d} saves in the last 30 days.
            </div>
          </div>
          <div className="text-right text-emerald-100/70 text-xs">
            Snapshot {fmtAgo(data.generated_at)}
          </div>
        </div>

        {/* Bucket chips */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          {['critical', 'at_risk', 'watch', 'healthy'].map(b => {
            const meta = BUCKET_META[b];
            const count = summary[b] || 0;
            const isActive = filter === b;
            return (
              <button
                key={b}
                onClick={() => setFilter(isActive ? 'all' : b)}
                className={`text-left rounded-lg px-3 py-2 border transition-colors ${
                  isActive
                    ? 'bg-white text-slate-900 border-white'
                    : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                }`}
              >
                <div className="text-2xl font-black tabular-nums">{count}</div>
                <div className={`text-[10px] uppercase tracking-wider font-bold ${isActive ? meta.textTone : 'opacity-80'}`}>
                  {meta.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: client list (sorted critical → healthy) */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><Heart size={14} className="text-red-600"/> In-force book by health</h3>
            <div className="text-xs text-slate-500">{filteredClients.length} {filter === 'all' ? 'clients' : BUCKET_META[filter]?.label}</div>
          </div>
          {filteredClients.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-slate-500">No clients in this bucket.</div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
              {filteredClients.slice(0, 100).map(c => (
                <ClientRow key={c.client_id} client={c} carriersByName={carriersByName}/>
              ))}
              {filteredClients.length > 100 && (
                <div className="px-5 py-2 text-xs text-slate-500 text-center">…and {filteredClients.length - 100} more</div>
              )}
            </div>
          )}
        </div>

        {/* Right: escalations + persistency */}
        <div className="space-y-4">
          {/* Escalations */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><AlertCircle size={14} className="text-amber-700"/> Escalations</h3>
              <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${escalations.length > 0 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                {escalations.length}
              </span>
            </div>
            {escalations.length === 0 ? (
              <div className="px-4 py-5 text-center text-xs text-slate-500">
                Nothing escalated. RETAIN is handling everything.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {escalations.map(e => (
                  <EscalationRow
                    key={e.id}
                    escalation={e}
                    onResolve={(status, outcome) => resolveEscalation(e, status, outcome)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Mission stats */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2"><TrendingUp size={14} className="text-emerald-700"/> Last 30 days</h3>
            </div>
            <div className="grid grid-cols-2 gap-px bg-slate-100">
              <div className="bg-white px-4 py-3 text-center">
                <div className="text-2xl font-black tabular-nums text-emerald-700">{summary.saves_30d}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">Saves</div>
              </div>
              <div className="bg-white px-4 py-3 text-center">
                <div className="text-2xl font-black tabular-nums text-amber-700">{summary.escalations_open}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">Open escalations</div>
              </div>
            </div>
            <div className="px-4 py-3 text-[11px] text-slate-500 leading-relaxed border-t border-slate-100">
              Saves are conservation_events with outcome='saved'. Lapses prevented save the agent the chargeback. Every save is money defended.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientRow({ client, carriersByName = {} }) {
  const meta = BUCKET_META[client.health.bucket] || BUCKET_META.healthy;
  const topFactor = (client.health?.factors || []).find(f => f.impact < 0);
  // Carrier catalog lookup: try the exact display name first, then
  // fall back to the first word (handles "Aetna Senior", "BCBS of TX",
  // "Mutual of Omaha Life" all coalescing to a catalog row).
  const carrierKey = String(client.carrier || '').toLowerCase().trim();
  const catalogHit = carrierKey
    ? (carriersByName[carrierKey] || carriersByName[carrierKey.split(/\s+/)[0]] || null)
    : null;
  const portalUrl = catalogHit?.portal_url || null;
  const autopayUrl = catalogHit?.autopay_url || portalUrl;
  // Show the payment link prominently on at-risk + critical clients
  // because the audit's complaint is "the operator has to copy the
  // carrier name into Google to find the portal." For healthy rows
  // we keep the link discoverable but small.
  const isUrgent = client.health.bucket === 'critical' || client.health.bucket === 'at_risk';
  return (
    <div className="px-5 py-3 flex items-center gap-3">
      <div className={`flex items-center justify-center h-12 w-12 rounded-lg ${meta.bgTone} ${meta.borderTone} border-2 font-bold tabular-nums text-sm shrink-0 ${meta.textTone}`}>
        {client.health.score}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 truncate">{client.name || 'Unknown'}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${meta.bgTone} ${meta.borderTone} ${meta.textTone}`}>
            {meta.label}
          </span>
          {client.open_conservation_case && (
            <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">
              Conservation
            </span>
          )}
          {/* Cross-sell gap chip — surfaces the # of missing coverages
              from client_coverages so the operator sees retention +
              cross-sell signals together. Hidden when gap data is
              missing (no client_coverages row) or fully covered. */}
          {client.crosssell_gaps != null && client.crosssell_gaps > 0 && (
            <span
              className="text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200"
              title={`Missing ${client.crosssell_gaps} of 9 cross-sellable coverages — dental, vision, life, accident, hospital indemnity, critical illness, cancer, disability, final expense`}
            >
              ✚ {client.crosssell_gaps} cross-sell
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5">
          {client.carrier && <span>{client.carrier}</span>}
          {client.policy && <span> · {client.policy}</span>}
          {client.days_since_issue != null && (
            <span> · day {client.days_since_issue} on book</span>
          )}
        </div>
        {topFactor && (
          <div className="text-[11px] text-slate-600 mt-0.5">⚠ {topFactor.label}</div>
        )}
      </div>
      {/* Carrier portal quick links — surfaces what the carriers catalog
          already knows. Per audit §11 + §15. Phone tap reveals on hover. */}
      {(autopayUrl || catalogHit?.customer_service_phone) && (
        <div className="shrink-0 flex flex-col items-end gap-1">
          {autopayUrl && (
            <a
              href={autopayUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={isUrgent ? `Open ${client.carrier} payment portal in a new tab` : `Open ${client.carrier} portal`}
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border whitespace-nowrap ${
                isUrgent
                  ? 'bg-violet-600 text-white border-violet-700 hover:bg-violet-700'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {isUrgent ? 'Pay portal ↗' : 'Portal ↗'}
            </a>
          )}
          {catalogHit?.customer_service_phone && (
            <a
              href={`tel:${catalogHit.customer_service_phone.replace(/[^\d+]/g,'')}`}
              title={`Call ${client.carrier} customer service`}
              className="text-[10px] text-slate-500 hover:text-slate-800 whitespace-nowrap"
            >
              {catalogHit.customer_service_phone}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function EscalationRow({ escalation, onResolve }) {
  const u = URGENCY_META[escalation.urgency] || URGENCY_META.normal;
  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border shrink-0 ${u.bg} ${u.border} ${u.text}`}>
          {u.label}
        </span>
        <div className="text-sm font-semibold text-slate-900 truncate flex-1">
          {escalation.client_name || (escalation.policy_ref || 'Unidentified client')}
        </div>
      </div>
      <div className="text-xs text-slate-600 mt-1.5">{escalation.summary}</div>
      {escalation.suggested_action && (
        <div className="text-[11px] text-slate-500 mt-1 italic">→ {escalation.suggested_action}</div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <button
          onClick={() => onResolve('resolved', 'agent_handled')}
          className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
        >
          <CheckCircle2 size={11}/> Handled
        </button>
        <button
          onClick={() => onResolve('dismissed', 'not_actionable')}
          className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100"
        >
          Dismiss
        </button>
        <span className="text-[10px] text-slate-400 ml-auto">{fmtAgo(escalation.created_at)}</span>
      </div>
    </div>
  );
}
